/**
 * RunCompareModal · Phase B · Step 4 · MLOps Hub
 *
 * Side-by-side overlay of two completed training runs:
 *   - epoch-axis SVG chart with three metric paths per run
 *     (loss · mAP50 · mAP50-95) drawn in emerald (Run A) vs amber (Run B)
 *   - delta-table at the bottom with the absolute best of each run
 *     and the absolute improvement %
 *
 * The selectors are constrained to runs that have status==="completed"
 * AND at least 1 metrics_history row — anything else can't render a
 * meaningful curve.
 */
import React, { useMemo, useState, useEffect } from "react";
import { X, GitCompareArrows, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";

const COLOR_A = "#6EE7B7";   // emerald-300
const COLOR_B = "#FBBF24";   // amber-400

export default function RunCompareModal({ runs, onClose }) {
  // Default-select the two newest completed runs with metrics.
  const eligible = useMemo(
    () => (runs || [])
      .filter((r) => r.status === "completed" && (r.metrics_history || []).length > 0),
    [runs],
  );
  const [aId, setAId] = useState(eligible[0]?.id || "");
  const [bId, setBId] = useState(eligible[1]?.id || eligible[0]?.id || "");

  useEffect(() => {
    if (!aId && eligible[0]) setAId(eligible[0].id);
    if (!bId && eligible[1]) setBId(eligible[1].id);
  }, [eligible, aId, bId]);

  const runA = eligible.find((r) => r.id === aId);
  const runB = eligible.find((r) => r.id === bId);

  return (
    <div
      data-testid="run-compare-modal"
      className="fixed inset-0 z-50 bg-background/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-card border border-border rounded-sm max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-secondary/30">
          <div className="flex items-center gap-2">
            <GitCompareArrows className="w-4 h-4 text-primary" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Phase B · Step 4 · MLOps Hub
              </div>
              <div className="font-display font-black uppercase tracking-widest text-sm">
                Run Comparison
              </div>
            </div>
          </div>
          <Button
            data-testid="run-compare-close"
            size="sm" variant="ghost" onClick={onClose}
            className="h-7 w-7 p-0"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {eligible.length < 2 ? (
          <div data-testid="run-compare-empty" className="px-5 py-8 text-center text-muted-foreground font-mono text-xs">
            Need at least 2 completed runs with epoch metrics to compare.
            Currently have {eligible.length}.
          </div>
        ) : (
          <>
            {/* Selectors */}
            <div className="grid sm:grid-cols-2 gap-3 px-5 py-3 border-b border-border bg-primary/[0.02]">
              <RunPicker
                label="Run A"
                color={COLOR_A}
                value={aId} onChange={setAId}
                options={eligible} otherId={bId}
                testId="run-compare-picker-a"
              />
              <RunPicker
                label="Run B"
                color={COLOR_B}
                value={bId} onChange={setBId}
                options={eligible} otherId={aId}
                testId="run-compare-picker-b"
              />
            </div>

            {/* Overlay chart */}
            {runA && runB && (
              <>
                <div className="px-5 py-4">
                  <OverlayChart runA={runA} runB={runB} />
                </div>

                {/* Winner banner */}
                <WinnerStrip runA={runA} runB={runB} />

                {/* Side-by-side metric grid */}
                <div className="grid sm:grid-cols-2 gap-3 px-5 pb-5">
                  <RunMetricCard run={runA} color={COLOR_A} label="A" />
                  <RunMetricCard run={runB} color={COLOR_B} label="B" />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function RunPicker({ label, color, value, onChange, options, otherId, testId }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
        <span
          className="inline-block w-2 h-2 rounded-full"
          style={{ background: color }}
        />
        {label}
      </span>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-background border border-border rounded-sm px-2 py-1.5 text-sm font-mono"
      >
        {options.map((r) => (
          <option key={r.id} value={r.id} disabled={r.id === otherId}>
            {(r.created_at || "").slice(5, 19).replace("T", " ")} · {r.config?.model || "—"} ·
            mAP50 {(r.best_mAP50 || 0).toFixed(3)}
          </option>
        ))}
      </select>
    </label>
  );
}

function OverlayChart({ runA, runB }) {
  // Common epoch axis covers both runs.
  const ma = runA.metrics_history || [];
  const mb = runB.metrics_history || [];
  const maxEpoch = Math.max(
    ...ma.map((m) => m.epoch || 0),
    ...mb.map((m) => m.epoch || 0),
    1,
  );
  const maxLoss = Math.max(
    ...ma.map((m) => m.loss || 0),
    ...mb.map((m) => m.loss || 0),
    1.0,
  );
  const W = 720, H = 220, P = 30;
  const innerW = W - 2 * P, innerH = H - 2 * P;
  const xAt = (epoch) => P + (epoch / maxEpoch) * innerW;
  const yAt = (val, norm) => P + innerH - (val / norm) * innerH;

  const path = (m, key, norm) => m.map((p, i) =>
    `${i === 0 ? "M" : "L"} ${xAt(p.epoch || (i + 1)).toFixed(1)} ${yAt(p[key] || 0, norm).toFixed(1)}`
  ).join(" ");

  return (
    <div data-testid="run-compare-chart" className="space-y-2">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-56 bg-background/40 rounded-sm">
        {/* horizontal grid */}
        {[0.25, 0.5, 0.75].map((g) => (
          <line
            key={g}
            x1={P} x2={W - P}
            y1={P + innerH * (1 - g)} y2={P + innerH * (1 - g)}
            stroke="hsl(var(--border))" strokeWidth={0.5} strokeDasharray="2 2"
          />
        ))}
        {/* x-axis labels */}
        {Array.from({ length: Math.min(maxEpoch, 10) }).map((_, i) => {
          const e = Math.round(((i + 1) / Math.min(maxEpoch, 10)) * maxEpoch);
          return (
            <text key={i} x={xAt(e)} y={H - 8}
              fontSize="9" fill="hsl(var(--muted-foreground))"
              textAnchor="middle" fontFamily="monospace"
            >
              {e}
            </text>
          );
        })}
        <text x={W / 2} y={H - 1} fontSize="9" fill="hsl(var(--muted-foreground))"
              textAnchor="middle" fontFamily="monospace">epoch</text>

        {/* Run A · loss (dashed amber-ish but in A's color), mAP50 solid, mAP50-95 dim */}
        <path d={path(ma, "loss", maxLoss)}     stroke={COLOR_A} strokeWidth={1.0} fill="none" opacity={0.45} strokeDasharray="4 3" />
        <path d={path(ma, "mAP50", 1.0)}        stroke={COLOR_A} strokeWidth={1.8} fill="none" />
        <path d={path(ma, "mAP50_95", 1.0)}     stroke={COLOR_A} strokeWidth={1.2} fill="none" opacity={0.6} />
        {/* Run B */}
        <path d={path(mb, "loss", maxLoss)}     stroke={COLOR_B} strokeWidth={1.0} fill="none" opacity={0.45} strokeDasharray="4 3" />
        <path d={path(mb, "mAP50", 1.0)}        stroke={COLOR_B} strokeWidth={1.8} fill="none" />
        <path d={path(mb, "mAP50_95", 1.0)}     stroke={COLOR_B} strokeWidth={1.2} fill="none" opacity={0.6} />

        {/* y-axis tick labels (normalised mAP) */}
        {[0, 0.5, 1].map((g) => (
          <text key={g} x={P - 4} y={P + innerH * (1 - g) + 3}
                fontSize="9" fill="hsl(var(--muted-foreground))"
                textAnchor="end" fontFamily="monospace">{g.toFixed(1)}</text>
        ))}
      </svg>
      <div className="flex justify-center gap-5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
        <span><span className="inline-block w-3 h-0.5 mr-1.5" style={{ background: COLOR_A }} />A · mAP50</span>
        <span><span className="inline-block w-3 h-0.5 mr-1.5 opacity-60" style={{ background: COLOR_A }} />A · mAP50-95</span>
        <span><span className="inline-block w-3 h-0.5 mr-1.5 opacity-45 border-t border-dashed" style={{ borderColor: COLOR_A }} />A · loss</span>
        <span className="opacity-40">|</span>
        <span><span className="inline-block w-3 h-0.5 mr-1.5" style={{ background: COLOR_B }} />B · mAP50</span>
        <span><span className="inline-block w-3 h-0.5 mr-1.5 opacity-60" style={{ background: COLOR_B }} />B · mAP50-95</span>
        <span><span className="inline-block w-3 h-0.5 mr-1.5 opacity-45 border-t border-dashed" style={{ borderColor: COLOR_B }} />B · loss</span>
      </div>
    </div>
  );
}

function WinnerStrip({ runA, runB }) {
  const a = runA.best_mAP50 || 0;
  const b = runB.best_mAP50 || 0;
  const winner = a === b ? null : (a > b ? "A" : "B");
  const delta = Math.abs(a - b);
  const pct = (a === 0 && b === 0) ? 0 : (delta / Math.max(a, b)) * 100;

  return (
    <div
      data-testid="run-compare-winner"
      className="px-5 py-2 border-y border-border bg-primary/[0.04] flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-widest"
    >
      {winner ? (
        <>
          <Crown className="w-3.5 h-3.5" style={{ color: winner === "A" ? COLOR_A : COLOR_B }} />
          <span style={{ color: winner === "A" ? COLOR_A : COLOR_B }}>
            Run {winner} wins · +{delta.toFixed(3)} mAP50 ({pct.toFixed(1)}% better)
          </span>
        </>
      ) : (
        <span className="text-muted-foreground">runs tied on mAP50</span>
      )}
    </div>
  );
}

function RunMetricCard({ run, color, label }) {
  const epochs = (run.config?.epochs) || 0;
  const done = run.epochs_done || 0;
  return (
    <div className="border border-border bg-background/40 rounded-sm p-3">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} />
        <span className="font-display font-black uppercase tracking-widest text-[11px]">
          Run {label}
        </span>
        <span className="font-mono text-[9px] text-muted-foreground">
          {(run.created_at || "").slice(5, 19).replace("T", " ")}
        </span>
      </div>
      <ul className="space-y-0.5 font-mono text-[10px]">
        <li>id: <span className="tabular-nums text-foreground/70">{run.id?.slice(0, 8)}</span></li>
        <li>model: <span className="text-foreground/90">{run.config?.model || "—"}</span></li>
        <li>epochs: <span className="tabular-nums">{done} / {epochs}</span></li>
        <li>mode: <span className="text-foreground/80">{run.mode}</span></li>
        <li>best mAP50: <span className="tabular-nums text-emerald-300">{(run.best_mAP50 || 0).toFixed(3)}</span></li>
        <li>best mAP50-95: <span className="tabular-nums text-emerald-300/80">{(run.best_mAP50_95 || 0).toFixed(3)}</span></li>
        <li>final loss: <span className="tabular-nums text-amber-300/80">{(run.current_loss || 0).toFixed(3)}</span></li>
      </ul>
    </div>
  );
}
